// eslint-disable-next-line jira/filename-case
import { useMemo } from 'react';

import {
	getEditionForProject,
	type ApplicationEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import {
	CORE_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	toProjectType,
	type ProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';

import { useSiteHasOnlyJira } from '@jira/growth__billing/src/controllers/explicitly-licensed-products/use-site-has-only-jira.tsx';
import { useProject } from '@jira/navigation-apps__sidebar-common/src/controllers/project/index.tsx';
import { operationsConverter } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/route-converters/operations/index.tsx';
import type { Edition, Product } from '@atlassian/edition-awareness/types';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';

export const EDITION_MAP: Partial<Record<ApplicationEdition, Edition>> = {
	FREE_EDITION: 'free',
	STANDARD_EDITION: 'standard',
	PREMIUM_EDITION: 'premium',
};

export const PRODUCT_MAP: Partial<Record<ProjectType, Product>> = {
	[SOFTWARE_PROJECT]: 'jira',
	[CORE_PROJECT]: 'jira',
	[PRODUCT_DISCOVERY_PROJECT]: 'jira-product-discovery',
	[SERVICE_DESK_PROJECT]: 'jira-service-management',
	[CUSTOMER_SERVICE_PROJECT]: 'jira-customer-service',
};

type ProductAndEditionInfo = {
	product?: Product;
	edition?: Edition;
	currentRoute?: 'ops';
	originalProjectTypeWasCore?: boolean;
};

/**
 * Condenses some logic from ProductAndEditionChecker. This is to be used
 * during the integration of Jira with the AudienceAndTiming code in Platform.
 * This will eventually be cleaned up as more EA code is shifted to Platform
 * @returns { product, edition, buttonProps }
 */
export const useProductAndEdition = (projectTypeFromQuery?: ProjectType): ProductAndEditionInfo => {
	const appEditions = useAppEditions();
	const { loading, data: project } = useProject();
	const siteHasOnlyJira = useSiteHasOnlyJira();
	const isOpsRoute = useIsActiveRoute(operationsConverter.routes);

	const projectType = useMemo(() => {
		if (projectTypeFromQuery) return projectTypeFromQuery;

		if (loading) return;

		const type = toProjectType(project?.type);

		// Unsafe exposure as we have further eligibility checks in Platform and can only fire exposure there
		const [jsmgrowth466Star] = UNSAFE_noExposureExp('jsmgrowth466_star');

		if (type) return type;

		// Ops/EOC routes are part of JSM — show edition-awareness as JSM when STAR experiment is enabled
		if (isOpsRoute && jsmgrowth466Star.get('cohort', 'not-enrolled') !== 'not-enrolled')
			return SERVICE_DESK_PROJECT;

		if (siteHasOnlyJira) return SOFTWARE_PROJECT;
	}, [projectTypeFromQuery, loading, project?.type, isOpsRoute, siteHasOnlyJira]);

	const applicationEdition = useMemo(() => {
		if (!projectType) return;

		if (siteHasOnlyJira) return getEditionForProject(SOFTWARE_PROJECT, appEditions);

		return getEditionForProject(projectType, appEditions);
	}, [appEditions, projectType, siteHasOnlyJira]);

	const { product, edition, originalProjectTypeWasCore } = useMemo(() => {
		if (!projectType || !applicationEdition) {
			// We don't have a product or edition, so return nothing
			return {};
		}

		// Unsafe exposure as we have further eligibility checks in Platform and can only fire exposure there
		const [twcg1563Config] = UNSAFE_noExposureExp('twcg-1563-remove-jira-only-restriction');

		if (projectType === CORE_PROJECT && !siteHasOnlyJira) {
			// FOR VARIATION:
			// JWM always maps to Jira
			// FOR CONTROL:
			// We are supporting only JWM implicit licensing when the parent license is JSW
			// Do not show on core products if site does not have only Jira
			// Cohorting and exposures are performed in Platform
			if (twcg1563Config.get('cohort', 'not-enrolled') !== 'not-enrolled') {
				return {
					originalProjectTypeWasCore: true,
					product: PRODUCT_MAP[projectType],
					edition: EDITION_MAP[applicationEdition],
				};
			}

			return {};
		}

		return {
			product: PRODUCT_MAP[projectType],
			edition: EDITION_MAP[applicationEdition],
		};
	}, [applicationEdition, projectType, siteHasOnlyJira]);

	return {
		product,
		edition,
		currentRoute: isOpsRoute ? 'ops' : undefined,
		originalProjectTypeWasCore,
	};
};
